import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { initPosthog } from './services/posthogService';
import { logInfo } from './services/loggerService';

import { SiteLayout } from './components/site/SiteLayout';
import { HomePage } from './pages/HomePage';
import { AboutPage } from './pages/AboutPage';
import { AmbassadorPage } from './pages/AmbassadorPage';
import { ChefsPage } from './pages/ChefsPage';
import { CodeOfConductPage } from './pages/CodeOfConductPage';
import { ConnectPage } from './pages/ConnectPage';
import { ContactPage } from './pages/ContactPage';
import { EatersPage } from './pages/EatersPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { SafetyPage } from './pages/SafetyPage';
import { TermsPage } from './pages/TermsPage';

import './styles/global.scss';

// Bootstrap PostHog before any component renders — no-op when
// VITE_POSTHOG_KEY is unset. See services/posthogService.ts for details.
initPosthog('www');

function AppContent() {
  useEffect(() => {
    const version = import.meta.env.VITE_VERSION;
    console.log(`Version: ${version}`);
    logInfo(`App loaded - Version: ${version}`);
  }, []);

  return (
    <BrowserRouter>
      {/* Inner Suspense boundary around the page <Switch>. i18n is
          configured with `useSuspense: true` and only preloads `common` +
          `errors` — every per-screen namespace lazy-loads on first
          `useTranslation('ns')` call. Without this boundary, those
          suspends bubble to the root Suspense in main.tsx, which would
          unmount the entire <AppContent> and re-run this bootstrap. */}
      <Suspense
        fallback={
          <div className="flex h-screen items-center justify-center bg-background" />
        }
      >
        <Switch>
          {/* Marketing pages — all wrapped in SiteLayout (header + footer). */}
          <Route exact path="/">
            <SiteLayout><HomePage /></SiteLayout>
          </Route>
          <Route exact path="/about">
            <SiteLayout><AboutPage /></SiteLayout>
          </Route>
          <Route exact path="/ambassador">
            <SiteLayout><AmbassadorPage /></SiteLayout>
          </Route>
          <Route exact path="/chefs">
            <SiteLayout><ChefsPage /></SiteLayout>
          </Route>
          <Route exact path="/code-of-conduct">
            <SiteLayout><CodeOfConductPage /></SiteLayout>
          </Route>
          <Route exact path="/connect">
            <SiteLayout><ConnectPage /></SiteLayout>
          </Route>
          <Route exact path="/contact">
            <SiteLayout><ContactPage /></SiteLayout>
          </Route>
          <Route exact path="/eaters">
            <SiteLayout><EatersPage /></SiteLayout>
          </Route>
          <Route exact path="/how-it-works">
            <SiteLayout><HowItWorksPage /></SiteLayout>
          </Route>
          <Route exact path="/privacy">
            <SiteLayout><PrivacyPage /></SiteLayout>
          </Route>
          <Route exact path="/safety">
            <SiteLayout><SafetyPage /></SiteLayout>
          </Route>
          <Route exact path="/terms">
            <SiteLayout><TermsPage /></SiteLayout>
          </Route>
        </Switch>
      </Suspense>
    </BrowserRouter>
  );
}

// TanStack Query is retained for `useSubmitContactForm` (the only mutation on
// the marketing site). The custom retry override that filtered 404s (via
// apiService's `.status`) is gone — webFormService uses raw fetch and doesn't
// stamp `.status`, so the default 3-retry policy is fine here. Contact-form
// mutations don't retry by default anyway (useMutation has retry: 0 unless
// overridden).
const queryClient = new QueryClient();

declare global {
  interface Window {
    __hpBootDone?: () => void;
  }
}

const App: React.FC = () => {
  // Clear the boot watchdog (set in index.html) — if we got here, React
  // mounted and the silent-white-screen fallback is no longer needed.
  useEffect(() => {
    window.__hpBootDone?.();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
};

export default App;
